import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { SITE_EDITORS, RESERVATION_ROLES } from "@/lib/site";
import { Logo } from "@/lib/brand";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { BackButton } from "@/components/BackButton";

const OWN_BACK = /^\/(plataforma|vendas|obras)\/./;
const GATED = ["/equipa", "/stock", "/meu-site", "/carta", "/reservas", "/pedidos-orcamento", "/marketing"];
import {
  House, Users, Package, FileText, Receipt, CreditCard, SignOut, Sparkle, UsersThree, HardHat, ChartLineUp,
  Globe, ForkKnife, CalendarCheck, CashRegister, Wallet, ChartBar, Cube, HourglassMedium, Megaphone, ClockCounterClockwise, Gear, ClipboardText,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MAIN_NAV = [
  { to: "/", label: "Painel", icon: House, testid: "nav-dashboard", end: true },
  { to: "/vendas", label: "Vendas", icon: Receipt, testid: "nav-sales", module: "sales" },
  { to: "/caixa", label: "Caixa", icon: CashRegister, testid: "nav-cash", module: "cash" },
  { to: "/despesas", label: "Despesas", icon: Wallet, testid: "nav-expenses", module: "expenses" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients", module: "clients" },
  { to: "/orcamentos", label: "Orçamentos", icon: FileText, testid: "nav-quotes", module: "quotes" },
  { to: "/produtos", label: "Produtos", icon: Package, testid: "nav-products" },
  { to: "/pagamentos", label: "Recebimentos", icon: CreditCard, testid: "nav-payments", module: "sales" },
  { to: "/relatorios", label: "Relatórios", icon: ChartBar, testid: "nav-reports", module: "reports" },
];
const SECTOR_NAV = [
  { to: "/obras", label: "Obras", icon: HardHat, testid: "nav-works", module: "works" },
  { to: "/stock", label: "Stock", icon: Cube, testid: "nav-stock", module: "stock" },
];

const ROLE_LABELS = {
  owner: "Proprietário", admin: "Administrador", accountant: "Contabilista",
  works_manager: "Gestor de obras", worker: "Trabalhador", salesperson: "Vendedor", client: "Cliente",
};

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-primary-foreground [&_svg]:text-mint" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
  }`;

function useNewCount(enabled, endpoint) {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get(endpoint).then(({ data }) => setCount(data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("reservations-updated", load);
    return () => { clearInterval(t); window.removeEventListener("reservations-updated", load); };
  }, [enabled, pathname, endpoint]);
  return count;
}

function GroupLabel({ children }) {
  return <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{children}</div>;
}

function NavItems({ items }) {
  return items.map((i) => (
    <NavLink key={i.to} to={i.to} end={i.end} data-testid={i.testid} className={linkCls}>
      <i.icon size={19} /> {i.label}
      {i.badge > 0 && <span className="ml-auto rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`${i.testid}-badge`}>{i.badge}</span>}
    </NavLink>
  ));
}

function useNav() {
  const { user } = useAuth();
  const org = user?.organization || {};
  const mods = org.modules || [];
  const has = (m) => !m || mods.includes(m);
  const role = user?.role;
  const canInbox = has("site") && RESERVATION_ROLES.includes(role);
  const canReservations = canInbox && org.sector === "restauracao" && !!user?.features?.reservas;
  const canQuotes = canInbox && org.sector === "construcao";
  const newRes = useNewCount(canReservations, "/site/reservations/new-count");
  const newQuotes = useNewCount(canQuotes, "/site/quote-requests/new-count");
  const main = MAIN_NAV.filter((i) => has(i.module));
  const sector = SECTOR_NAV.filter((i) => has(i.module));
  const site = [];
  if (has("site") && SITE_EDITORS.includes(role)) {
    site.push({ to: "/meu-site", label: "O meu site", icon: Globe, testid: "nav-site" });
    site.push({ to: "/carta", label: "Carta/Produtos", icon: ForkKnife, testid: "nav-menu" });
  }
  if (canReservations) site.push({ to: "/reservas", label: "Reservas", icon: CalendarCheck, testid: "nav-reservations", badge: newRes });
  if (canQuotes) site.push({ to: "/pedidos-orcamento", label: "Pedidos de orçamento", icon: ClipboardText, testid: "nav-quote-requests", badge: newQuotes });
  const marketing = has("marketing") && SITE_EDITORS.includes(role)
    ? [{ to: "/marketing", label: "Planeamento", icon: Megaphone, testid: "nav-marketing", end: true },
      { to: "/marketing/historico", label: "Histórico", icon: ClockCounterClockwise, testid: "nav-marketing-history" },
      { to: "/marketing/configuracoes", label: "Configurações", icon: Gear, testid: "nav-marketing-settings" }]
    : [];
  const account = [
    ...(user?.features?.equipa ? [{ to: "/equipa", label: "Equipa", icon: UsersThree, testid: "nav-team" }] : []),
    { to: "/o-meu-plano", label: "O meu plano", icon: Sparkle, testid: "nav-plan" },
  ];
  if (user?.is_platform_admin) account.push({ to: "/plataforma", label: "Super-admin", icon: ChartLineUp, testid: "nav-platform" });
  const bottomKeys = ["/", "/vendas", "/caixa", sector[0]?.to || "/clientes", sector[0] ? "/clientes" : "/despesas"];
  const all = [...main, ...sector, ...site, ...marketing, ...account];
  const bottom = bottomKeys.map((k) => all.find((i) => i.to === k)).filter(Boolean);
  return { org, main, sector, site, marketing, account, all, bottom, newRes, comingSoon: org.coming_soon || [] };
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { entitlement } = useSubscription();
  const company = useCompany();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nav = useNav();
  const orgName = nav.org.name || "GESTA CV";
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const trialing = entitlement?.state === "trialing";
  const doLogout = async () => { await logout(); navigate("/login"); };
  const extra = nav.all.filter((i) => !nav.bottom.includes(i));

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card md:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <Link to="/" aria-label="GESTA CV — painel"><Logo className="h-11 w-auto" testid="sidebar-logo" /></Link>
        </div>
        <div className="-mt-2 px-6 pb-1 text-[11px] text-muted-foreground truncate" data-testid="sidebar-org">
          {orgName}{nav.org.sector_label ? ` · ${nav.org.sector_label}` : ""}
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
          <NavItems items={nav.main} />
          {(nav.sector.length > 0 || nav.comingSoon.length > 0) && <GroupLabel>{nav.org.sector_label || "Setor"}</GroupLabel>}
          <NavItems items={nav.sector} />
          {nav.comingSoon.map((c) => (
            <div key={c} className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground/60" data-testid="nav-coming-soon">
              <HourglassMedium size={19} /> <span className="truncate">{c}</span>
              <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">Em breve</span>
            </div>
          ))}
          {nav.site.length > 0 && <GroupLabel>Site público</GroupLabel>}
          <NavItems items={nav.site} />
          {nav.marketing.length > 0 && <GroupLabel>Marketing</GroupLabel>}
          <NavItems items={nav.marketing} />
          <GroupLabel>Conta</GroupLabel>
          <NavItems items={nav.account} />
        </nav>
        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-accent">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold">{initials}</div>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-sm font-medium">{user?.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={doLogout} className="text-destructive">
                <SignOut size={16} className="mr-2" /> Terminar sessão
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/90 px-4 py-3 backdrop-blur-xl md:hidden">
        <Logo className="h-9 w-auto" testid="mobile-header-logo" />
        <div className="flex items-center gap-2">
          {nav.site.some((i) => i.to === "/reservas") && (
            <button onClick={() => navigate("/reservas")} data-testid="mobile-reservations-btn"
              className="relative flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
              <CalendarCheck size={18} />
              {nav.newRes > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid="mobile-reservations-badge">{nav.newRes}</span>}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-menu-trigger" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold">{initials}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[75vh] w-60 overflow-y-auto">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {extra.map((i) => (
                <DropdownMenuItem key={i.to} data-testid={`mobile-${i.testid}`} onClick={() => navigate(i.to)}>
                  <i.icon size={16} className="mr-2" /> {i.label} {i.badge > 0 && `(${i.badge})`}
                </DropdownMenuItem>
              ))}
              {nav.comingSoon.map((c) => (
                <DropdownMenuItem key={c} disabled><HourglassMedium size={16} className="mr-2" /> {c} · Em breve</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="mobile-logout-btn" onClick={doLogout} className="text-destructive">
                <SignOut size={16} className="mr-2" /> Terminar sessão
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="px-4 pb-24 pt-4 md:ml-64 md:px-8 md:pb-10 md:pt-8">
        {GATED.includes(pathname) && !nav.all.some((i) => i.to === pathname) && <Navigate to="/" replace />}
        {pathname !== "/" && !OWN_BACK.test(pathname) && (
          <BackButton fallback={pathname.split("/").slice(0, -1).join("/") || "/"} className="-ml-3 mb-2" testid="page-back-btn" />
        )}
        {trialing && (
          <button onClick={() => navigate("/o-meu-plano")} data-testid="trial-banner"
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-accent px-4 py-2.5 text-sm font-medium text-accent-foreground transition-colors hover:bg-primary hover:text-primary-foreground">
            <Sparkle size={16} weight="fill" />
            Teste grátis — {entitlement?.days_left} dia(s) restantes. Escolha o seu plano para não perder o acesso.
          </button>
        )}
        {children}
        <p className="mt-12 text-center text-[11px] text-muted-foreground/70" data-testid="app-company-line">
          {company.product_name} · {company.short_name || "Tavares Groupe"}
        </p>
      </main>

      <nav className="fixed bottom-0 z-50 flex w-full items-center justify-around border-t border-border bg-card/90 py-1.5 backdrop-blur-xl md:hidden">
        {nav.bottom.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={`bottom-${item.testid}`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-1 text-[10px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <item.icon size={22} /> {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
